import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';
import { toast } from 'sonner';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    toast.success('Message sent successfully! We\'ll respond within 24 hours.');
    setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
  };

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Us',
      details: 'info@trademarkassist.co.uk',
      description: 'Send us your queries anytime',
      link: 'mailto:info@trademarkassist.co.uk',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Call Us',
      details: '+44 20 1234 5678',
      description: 'Mon-Fri, 9AM-6PM',
      link: 'tel:+442012345678',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Visit Us',
      details: '20 St Thomas St, London SE1 9RS, United Kingdom',
      description: 'Our head office location',
      link: 'https://maps.google.com/?q=20+St+Thomas+St,+London+SE1+9RS',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Working Hours',
      details: 'Mon-Fri: 9AM-6PM',
      description: 'Weekend: Closed',
      link: null,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+353', country: 'Ireland' }
  ];

  const features = [
    { icon: <CheckCircle className="w-5 h-5" />, text: '24-hour response time' },
    { icon: <CheckCircle className="w-5 h-5" />, text: 'Expert trademark advice' },
    { icon: <CheckCircle className="w-5 h-5" />, text: 'Free initial consultation' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with UK Flag Background */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Animated UK Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&h=1080&fit=crop"
            alt="London Background"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#012169]/90 via-background/95 to-[#C8102E]/90"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-4 bg-gradient-to-r from-[#012169] to-[#C8102E] text-white">We're Here to Help</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Get in Touch with Our Experts
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Have questions about trademark registration? Our expert team is ready to assist you with professional advice and guidance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section - Split Layout */}
      <section className="py-20 px-4 bg-gradient-to-br from-background via-accent/10 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="p-8 lg:p-10 border-2 border-border shadow-2xl bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#012169] to-[#C8102E] rounded-xl flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">Send Us a Message</h2>
                    <p className="text-sm text-muted-foreground">We'll respond within 24 hours</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="border-2 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="border-2 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Contact Number *</Label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                        <SelectTrigger className="w-[140px] border-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code} ({item.country})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="1234567890"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="flex-1 border-2 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us more about your inquiry..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="border-2 focus:border-primary"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white shadow-lg group"
                  >
                    <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                    Send Message
                  </Button>
                </form>
              </Card>
            </motion.div>

            {/* Right: Contact Info & Features */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Why Contact Us */}
              <Card className="p-8 border-2 border-primary/20 bg-gradient-to-br from-[#012169]/5 to-[#C8102E]/5">
                <h3 className="text-2xl font-bold text-foreground mb-4">Why Contact Us?</h3>
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#012169] to-[#C8102E] rounded-full flex items-center justify-center text-white">
                        {feature.icon}
                      </div>
                      <span className="text-foreground font-medium">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Contact Info Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <Card className="p-6 border-2 border-border hover:border-primary/50 hover:shadow-xl transition-all duration-300 group bg-white">
                      <div className={`w-14 h-14 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                        {info.icon}
                      </div>
                      <h3 className="font-bold text-foreground mb-1">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} className="text-primary hover:underline font-semibold block mb-1">
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-foreground font-semibold mb-1">{info.details}</p>
                      )}
                      <p className="text-sm text-muted-foreground">{info.description}</p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
